define('routes',[
    'extensions/sammy.EmbeddedLocationProxy',
    'lib/sammy',
    'lib/knockout',
    'lib/lodash4',
    'services/loggingService',
    'services/authService',
    'services/featureToggleService',
    'services/navHelper',
    'services/userService',
    'services/routeService',
    'services/qm/qualityAuth',
    'services/toastService',

    /* View Models */
    'viewModels/appView',

    /* Route definitions */
    'infoArchitecture/composerRoutes',
    'infoArchitecture/coreRoutes',
    'infoArchitecture/qmRoutes',
    'infoArchitecture/contentManagementRoutes'
], function (
    EmbeddedLocationProxy,
    sammy,
    ko,
    _,
    log,
    authService,
    featureToggleService,
    navHelper,
    userService,
    routeService,
    qualityAuth,
    toastService,

    appView,

    composerRoutes,
    coreRoutes,
    qmRoutes,
    contentManagementRoutes
) {
    'use strict';

    // ===================================================================================
    // Route Authorizations (parsed during sammy configuration)
    // ===================================================================================
    //   key is path to check, value is authorizations that allow access
    //   unchecked paths are allowed to all

    var requiredAuthorizations = [
        {
            path: '#dashboard.*$',
            permissions: ['role:qualityEvaluator', 'role:qualityAdmin', 'perm:quality:evaluation:add',
                'perm:quality:calibration:view', 'perm:quality:evaluation:editScore', 'perm:quality:evaluation:view',]
        },
        {
            path: '#admin/interactions.*$',
            permissions: [{
                any: ['perm:conversation:communication:view', 'role:qualityEvaluator', 'role:qualityAdmin', 'perm:quality:evaluation:add', 'perm:quality:calibration:view', 'perm:quality:evaluation:editScore'],
                ignoreDivisions: true
            }]
        },
        {
            path: '#admin/interactions/.*/evaluationSummary.*$',
            permissions: ['role:qualityAdmin', 'perm:quality:evaluation:add', 'perm:quality:evaluation:delete', 'perm:quality:calibration:add']
        },
        {
            path: '#admin/evaluationForms.*$',
            permissions: ['role:qualityAdmin', 'perm:quality:evaluationform:add', 'perm:quality:evaluationform:edit', 'perm:quality:evaluationform:delete']
        },
        {
            path: '#admin/surveyForms.*$',
            permissions: ['perm:quality:surveyform:add', 'perm:quality:surveyform:edit', 'perm:quality:surveyform:delete']
        }
    ];


    // ===================================================================================
    // Authentication
    // ===================================================================================

    /**
     * Find the default route for the user.
     */
    var getDefaultRoute = function () {
        var defaultRoute;

        if (authService.hasAnyAuthorization(['perm:quality:evaluation:add', 'perm:quality:calibration:view', 'perm:quality:evaluation:editScore'])) {
            defaultRoute = "#dashboard";
        } else if (authService.hasAuthorization({ all: ['perm:routing:agent:onqueue'], ignoreDivisions: true }) || qualityAuth.canViewEvaluation()) {
            // TODO: change this when we have real agents
            // TODO: See https://inindca.atlassian.net/browse/UI-134 for status.
            defaultRoute = '#/analytics/agents/performance/me';
        } else if (authService.hasAuthorization('perm:content_management_user')) {
            defaultRoute = "#contentManagement/dashboard";
        } else if (authService.hasAuthorization('perm:content_management_admin')) {
            defaultRoute = "#admin/contentManagement/workspaces";
        } else {
            defaultRoute = "#interactions";
        }

        return defaultRoute;
    };

    /*
     * Logic that helps route the user to the correct page once they have transitioned
     * into an authenticated or unauthenticated state. If the user has a route, we'll
     * leave them be -- otherwise we send them to their default route. If not authenticated,
     * we'll just send them to #noop.
     */
    userService.currentUser.subscribe(function (currentUser) {
        if (currentUser && window.location.hash === '') {
            navHelper.navigateTo(getDefaultRoute());
        } else if (!currentUser) {
            navHelper.navigateTo('noop');
        }
    });

    /*
     * Handling for events that need to occur on page unload.
     * Routes will register an onLeave handler when they run.
     * If the handler runs and does not cancel the navigation event
     * it will be unregistered afterward.
     */
    var onLeaveHandler;
    function handleOnLeave() {
        if (onLeaveHandler) {
            var result = onLeaveHandler();
            if (result !== false) {
                // if the navigation event wasn't cancelled, clear the handler
                onLeaveHandler = null;
            }
            return result;
        }
    }

    var registerRoutes = function (sammyInstance) {
        var routeConfigs = Array.prototype.slice.call(arguments, 1);

        //Combine the route configs into a single object
        routeConfigs.unshift({});
        var unifiedRoutes = _.assign.apply(this, routeConfigs);

        // Turn the object based config into an array of routes
        unifiedRoutes = _.map(unifiedRoutes, function (value, key) {
            value.hash = key;
            return value;
        });

        // Sort the routes by hash in reverse lexographic order
        // This is done so that less specific routes will be registered
        // later with sammy. i.e. #foo will be registered after #foo/bar
        // This ensures generally correct behavior when sammy converts the
        // hash into a starts-with style of regex
        unifiedRoutes.sort(function (route1, route2) {
            if (route1.hash == route2.hash) {
                return 0; //This should never happen
            } else if (route1.hash > route2.hash) {
                return -1;
            } else {
                return 1;
            }
        });

        log.trace('Registering Routes:');
        _.each(unifiedRoutes, function (route) {
            //Useful to know the ordering of route registration in Sammy
            log.trace(route.hash);

            // This will make the canonical hash for the page data the
            // last route mapped to it, which should be a 'parent' route
            // since we register shorter substring routes later
            if (route.pageInfo) {
                route.pageInfo._hash = route.hash;
                route.pageInfo.isStaticBreadcrumb = route.isStaticBreadcrumb;
            }

            sammyInstance.get(route.hash, function (ctx) {
                routeService
                    .pathParameters(ctx.params)
                    .pageInfo(route.pageInfo)
                    .helpURL(route.hash);

                // Update the fixedHeight class for views that are intended to
                // be consumed with a fixed height.
                if(route.fixedHeight !== undefined){
                    // The double NOT(!) opperator is essentially casting fixedHeight
                    // to a true boolean value before setting the fixedHeight observable.
                    appView.fixedHeight(!!route.fixedHeight);
                } else {
                    appView.fixedHeight(false);
                }

                if (route.hScroll !== undefined) {
                    appView.hScroll(!!route.hScroll);
                } else {
                    appView.hScroll(false);
                }

                if (route.fillContent !== undefined) {
                    appView.fillContent(!!route.fillContent);
                } else {
                    appView.fillContent(false);
                }

                route.handler.apply(this, arguments);
                if (route.before) {
                    log.warning('The "before" option for an individual route has been deprecated in favor of onLeave. See UI-1472 for details');
                }
                if (route.onLeave || route.before) {
                    onLeaveHandler = route.onLeave || route.before;
                }
            });
        });
    };

    /**
     * Set up permission checks for routes. If a user is not authorized
     * to access a route, they are redirected to the root of the app
     * (which will redirect them to their default route)
     */
    function addAuthorizationChecks(sammyInstance, requiredAuthorizations) {
        function hasPermissions (perm) {
            return authService.hasAuthorization(perm, true);
        }

        _.each(requiredAuthorizations, function (permittedAuth) {
            sammyInstance.before(new RegExp(permittedAuth.path), function () {
                var permissionsOrRoles = permittedAuth.permissions || permittedAuth.roles;
                var isAuthorized;
                if (permittedAuth.requireAll) {
                    isAuthorized = _.every(permissionsOrRoles, hasPermissions);
                } else {
                    isAuthorized = _.some(permissionsOrRoles, hasPermissions);
                }
                if (!isAuthorized) {

                    // default when a permission/role is not defined in localization
                    // error.forbidden.role:someUndefinedRole
                    // error.forbidden.perm:component:someNewEntity:someNewAction
                    var forbiddenKey = 'error.forbidden.' + _.first(permissionsOrRoles);
                    if (!ko.i18n.keyExists(forbiddenKey)) {
                        forbiddenKey = 'error.forbidden';
                    }

                    toastService.notifyMajorAlert(forbiddenKey);

                    //If user is not authorized, remove the hash and let the user be re-directed
                    //to their default view
                    navHelper.navigateTo(getDefaultRoute());
                    return false;
                }
            });
        });
    }

    /**
     * The routes module registers routing information with the route system and exports
     * the generated route object.
     *
     * Actual definition of route behaviors lives in separate config files under the
     * infoArchitecture subdirectory, and are registered here via the 'registerRoutes' function.
     * @exports routes
     */
    var sammyInstance;
    var routes = sammy(function () {
        var self = this;
        sammyInstance = self;

        // ===============================================================================
        // Sammy Setup
        // ===============================================================================

        sammyInstance.disable_push_state = true;
        sammyInstance.setLocationProxy(new EmbeddedLocationProxy(self));

        /**
         * This function will be executed for every page request and primarily checks to
         * see if the current user is logged in. If they are not, they will be redirected
         * to the login page.
         */
        self.around(function (routeCallback) {
            routeCallback();

            // Update the route service state.
            routeService.route(window.location.hash);
        });

        /**
         * Sammy doesn't play particularly nice with Knockout with regards to processing forms.
         *
         * REFERENCE: http://stackoverflow.com/questions/14861461/weird-redirect-using-data-bind-submit-sammy-js-and-knockout-js-together
         */
        self._checkFormSubmission = function () {
            return (false);
        };

       
        addAuthorizationChecks(self, requiredAuthorizations);

        // Handle any registered onLeave event before a new route
        sammyInstance.before(handleOnLeave);

        /**
         * Register routes
         */
        registerRoutes(
            self,
            composerRoutes,
            coreRoutes,
            qmRoutes,
            contentManagementRoutes
        );

    });


    // TODO: refactor routing so component loading doesn't require monkeypatching sammy route object
    routes.addAuthorizationChecks = _.partial(addAuthorizationChecks, sammyInstance);

    function callComponentHandler(componentName, routeContext, componentView, handler, ctx) {
        function activateSubView(parent, name) {
            var cacheActiveViews = routeContext && routeContext.cacheRootViewOnNavigate; // Allow subViews to change without reloading parents
            return cacheActiveViews && parent.subViews.getActiveView() === name ?
                parent.subViews[name]() :
                parent.subViews.setActiveView(name);
        }

        var appMain = activateSubView(appView, 'appMain');
        var privilegedView = activateSubView(appMain, 'privilegedView');

        var currentActiveComponent = privilegedView.activeComponent();

        if (!currentActiveComponent || currentActiveComponent.name !== componentName) {
            privilegedView.activeComponent({
                data: componentView,
                name: componentName,
            });
        }

        handler(routeContext, ctx);
    }

    routes.registerComponentRoutes = function (componentName, componentRouteContext, componentView, componentRoutes) {
        _.each(componentRoutes, function (routeDetails) {
            log.debug('Setting up component route', routeDetails.hash);
            routeDetails.handler = _.partial(callComponentHandler,
                    componentName,
                    componentRouteContext,
                    componentView,
                    routeDetails.handler
            );
        });

        registerRoutes(sammyInstance, componentRoutes);
    };

    //
    // Set up a run method that registers the catch-all route.
    //
    var registerDefaultHandler = _.once(function () {
        sammyInstance.get('', function () {
            navHelper.redirectTo(getDefaultRoute());
        });
    });

    var originalRun = routes.run;
    routes.run = function () {
        registerDefaultHandler();
        originalRun.apply(routes);
    };

    return routes;
});

